import React, { useContext, useMemo, useCallback } from "react";
import "./FiltersMenuPopup.scss";
import { FiltersContext } from "../../FilterProvider/FilterProvider";
import {
  filterTypes,
  findingsQueryParams,
} from "../../../../Findings/FindingsUtils";
import useFindingsQueryParam from "../../../../Findings/FindingsData/useFindingsQueryParam";
import {
  UPDATE_FILTER_V3,
  type UpdateFilterResponse,
  type UpdateFilterVars,
} from "../../../../Findings/FindingsFilters/FindingsFilterApi";
import DeleteFilterModal from "../../FilterDependencies/DeleteFilterModal";
import { showToast } from "../../../../Forms/Dialogs/Toast/Toast";
import { useRouteMatch } from "react-router-dom";
import { filterTabs } from "../../../AppViewUtils";
import { useFormContext } from "react-hook-form";
import FilterSelection, {
  FilterActionButton,
  type FilterRow,
} from "./FilterSelection";
import {
  AppPreferencesContext,
  userActionsEnum,
} from "../../../../General/AppPreferencesProvider";
import FilterSaveAs from "../../../../Findings/FindingsFilters/FilterSaveAs/FilterSaveAs";
import {
  TextButton,
  useMutationWrapper,
  ModalContext,
  showErrorDialog,
} from "@SeemplicityDev/shared";
import type { FilterObject } from "../../CommonFilterTypes";
import { FILTER_RENAME_NOTICE } from "../../../../messages";
import useSavedFilterType from "../../useSavedFilterType";

export interface FiltersMenuPopupProps {
  toggleIsOpen: (isOpen: boolean) => void;
  setDefaultsForScopeAndFilter?: boolean;
  displayMenuHeader?: boolean;
}

const FiltersMenuPopup: React.FC<FiltersMenuPopupProps> = ({
  toggleIsOpen,
  setDefaultsForScopeAndFilter,
  displayMenuHeader,
}) => {
  const {
    filtersState: { filtersList, selectedFilter },
    updateFiltersState,
    lsFilterKey,
    filtersEntity,
  } = useContext(FiltersContext);
  const savedFilterType = useSavedFilterType(filtersEntity);
  let match = useRouteMatch<{ item: string }>("/:item");
  const { isAllowed, isSelfActionAllowed } = useContext(AppPreferencesContext);
  const { openModal, closeModal } = useContext(ModalContext);

  const [updateFilter] = useMutationWrapper<
    UpdateFilterResponse,
    UpdateFilterVars
  >(UPDATE_FILTER_V3, {
    refetchQueries: ["filters"],
  });

  const { clearQueryParam, search } = useFindingsQueryParam(null);
  const { reset } = useFormContext();

  const onFilterDeleted = (name?: string) => {
    let newState = {};
    newState = { filterToExecute: null, selectedFilter: null };
    updateFiltersState(newState);
    localStorage.removeItem(lsFilterKey);

    showToast({
      message: (
        <div className={"modal-text"}>
          The Filter <span className={"modal-live-text"}>{name}</span> was
          deleted successfully
        </div>
      ),
    });
  };

  const defaultFilter = useMemo(() => {
    return filtersList?.filter(
      (filter) => (filter as FilterRow)?.meta?.default
    )?.[0];
  }, [filtersList]);

  const onSelectFilter = (selectedOption: FilterObject) => {
    updateFiltersState({ shouldLoad: true });
    toggleIsOpen(false);

    if (!!search) {
      clearQueryParam([
        findingsQueryParams.FILTER_ID,
        findingsQueryParams.FILTER_TO_EXECUTE,
      ]);
    }
    if (setDefaultsForScopeAndFilter) {
      localStorage.setItem(lsFilterKey, JSON.stringify(selectedOption));
    }

    updateFiltersState({
      filterToExecute: selectedOption.value.query,
      selectedFilter: selectedOption,
      shouldLoad: false,
    });
  };

  const backToDefault = () => {
    onSelectFilter(defaultFilter!);
    updateFiltersState({ filtersMenuTab: filterTabs.RECOMMENDED });
  };

  const handleDeleteFilter = (
    event: React.MouseEvent<HTMLButtonElement>,
    filter: FilterRow
  ) => {
    event.stopPropagation();
    toggleIsOpen(false);
    const filterId = filter?.value?.id;
    if (!filterId) {
      showErrorDialog({ message: "Could not delete this filter." });
      return;
    }
    openModal({
      title: "Delete Filter",
      size: "m",
      className: "delete-filter-modal-dialog",
      overlay: true,
      component: (
        <DeleteFilterModal
          filterId={filterId}
          filterName={filter?.label}
          onDeleted={() => onFilterDeleted(filter?.label)}
        />
      ),
    });
  };

  const handleEditFilter = (
    event: React.MouseEvent<HTMLButtonElement>,
    filter: FilterRow
  ) => {
    event.stopPropagation();
    toggleIsOpen(false);
    const saveRename = (name: string) => {
      const filterId = filter?.value?.id;
      const filterData = filter?.value?.query;
      if (!filterId || !filterData) {
        showErrorDialog({ message: "Could not rename this filter." });
        return;
      }

      // Rename-only: the stored criteria are sent back unchanged, so V3 applies the new name
      // without the dependency guardrail. A colliding name is rejected by the server and
      // surfaces through .catch with the "already exists" message.
      updateFilter({
        variables: {
          id: filterId,
          name,
          filterData,
          filterType: savedFilterType,
          confirmed: false,
        },
      })
        .then((res) => {
          if (!res?.data?.update_finding_filter_v3?.ok) {
            // edge case - filter changed by sopmeone else between we got filters and trying to rename
            showErrorDialog({
              message: `Could not rename the filter "${filter?.label}". Reopen the filters menu and try again.`,
            });
            return;
          }
          closeModal();
          showToast({
            message: (
              <div className={"modal-text"}>
                The Filter was renamed to{" "}
                <span className={"modal-live-text"}>{name}</span>
              </div>
            ),
          });
        })
        .catch((err: { message?: string }) =>
          showErrorDialog({ message: err.message })
        );
    };

    openModal({
      title: "Edit Filter",
      size: "m",
      overlay: true,
      component: (
        <FilterSaveAs
          name={filter?.label}
          setValue={(_prop: string, name: string) => saveRename(name)}
          type={filterTypes.FILTER.toLowerCase()}
          filter_data={filter?.value?.query}
          notice={FILTER_RENAME_NOTICE}
        />
      ),
    });
  };

  const getActionButtons = useCallback(
    (filter: FilterRow): FilterActionButton[] => [
      ...(isAllowed(userActionsEnum.DELETE_FILTER) ||
      isSelfActionAllowed(
        userActionsEnum.SELF_DELETE_FILTER,
        filter?.meta?.created_by
      )
        ? [
            {
              title: "Delete Filter",
              icon: "seem-icon-bin",
              dataTestId: "delete-filter-item-btn",
              onClick: handleDeleteFilter,
            },
          ]
        : []),
      ...(isAllowed(userActionsEnum.EDIT_FILTER) ||
      isSelfActionAllowed(
        userActionsEnum.SELF_EDIT_FILTER,
        filter?.meta?.created_by
      )
        ? [
            {
              title: "Rename Filter",
              icon: "seem-icon-edit",
              dataTestId: "edit-filter-item-btn",
              onClick: handleEditFilter,
            },
          ]
        : []),
    ],
    []
  );

  const header = useMemo(() => {
    return (
      displayMenuHeader && (
        <div className={`filters-menu-popup-header`}>
          <h3 className={`filters-menu-title`}>Filter By</h3>
          <TextButton
            label={"Back to Default"}
            onClick={() => {
              if (selectedFilter?.value?.id === defaultFilter?.value?.id) {
                toggleIsOpen(false);
                reset();
                setTimeout(() => {
                  backToDefault();
                }, 0);
              } else {
                backToDefault();
              }
            }}
            disabled={!defaultFilter}
            data-analytics-event="user_click"
            data-analytics-props='{"name": "back_to_default_btn", "container": "filter_selection_dropdown"}'
          />
        </div>
      )
    );
  }, [displayMenuHeader]);

  return (
    <div
      className={`filters-menu-popup-wrapper filters-menu-popup-wrapper-${match?.params?.item}`}
    >
      <FilterSelection
        menuHeader={header}
        onSelect={onSelectFilter}
        getActionButtons={getActionButtons}
      />
    </div>
  );
};

export default FiltersMenuPopup;
